import { getOperationAST } from 'graphql'

import apolloClient, { normalizeApolloError } from './apolloClient'

import type { OperationVariables } from '@apollo/client'
import type { TypedDocumentNode } from '@graphql-typed-document-node/core'


export const DEFAULT_GRAPHQL_TIMEOUT_MS = 30_000

export type GraphQLRequestOptions = {
    accessToken?: string
    signal?: AbortSignal
    timeoutMs?: number
}

const createRequestSignal = (options: GraphQLRequestOptions) => {
    const controller = new AbortController()
    const timeoutId = window.setTimeout(
        () => controller.abort(new DOMException('GraphQL request timed out', 'TimeoutError')),
        options.timeoutMs ?? DEFAULT_GRAPHQL_TIMEOUT_MS,
    )
    const abortFromCaller = () => controller.abort(options.signal?.reason)

    if (options.signal?.aborted) {
        abortFromCaller()
    } else {
        options.signal?.addEventListener('abort', abortFromCaller, { once: true })
    }

    return {
        dispose: () => {
            window.clearTimeout(timeoutId)
            options.signal?.removeEventListener('abort', abortFromCaller)
        },
        signal: controller.signal,
    }
}

export const executeGraphQL = async <
    TResult,
    TVariables extends OperationVariables,
>(
    document: TypedDocumentNode<TResult, TVariables>,
    variables: TVariables,
    options: GraphQLRequestOptions = {},
): Promise<TResult> => {
    const requestSignal = createRequestSignal(options)
    const context = {
        fetchOptions: { signal: requestSignal.signal },
        headers: options.accessToken
            ? { Authorization: `Bearer ${options.accessToken}` }
            : undefined,
    }

    try {
        const operation = getOperationAST(document)
        if (operation?.operation === 'mutation') {
            const result = await apolloClient.mutate<TResult, TVariables>({
                context,
                mutation: document,
                variables,
            })
            if (!result.data) throw new Error('GraphQL mutation returned no data')
            return result.data
        }

        const result = await apolloClient.query<TResult, TVariables>({
            context,
            fetchPolicy: 'no-cache',
            query: document,
            variables,
        })
        if (!result.data) throw new Error('GraphQL query returned no data')
        return result.data
    } catch (error: unknown) {
        throw normalizeApolloError(error)
    } finally {
        requestSignal.dispose()
    }
}
